import * as React from 'react';
import {
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  AccordionActions,
  Divider,
  IconButton,
  makeStyles,
  CircularProgress,
  // Skeleton, // uncomment when moving to material UI v5 (see https://github.com/mui-org/material-ui/pull/22740)
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@material-ui/core';
import QueuedIcon from '@material-ui/icons/Pause';
import FinishedIcon from '@material-ui/icons/Done';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DownloadIcon from '@material-ui/icons/GetApp';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import FilterIcon from '@material-ui/icons/FilterList';
import { CsvExportRequestStatus, CsvExportRequest, CsvExportRequestList } from '@wooindex/common/types';
import { Link, useI18n } from '../i18n';
import clsx from 'clsx';
import SaveToDriveButton from './SaveToDriveButton';
// Remove when upgrading to material UI v5
import { Skeleton } from '@material-ui/lab';
import FlexFill from '../FlexFill';
import { useHasFeature } from 'components/FeatureFlags';
import { serializeFilter } from '@wooindex/common/filter';
import DeleteCsvExportDialog from './DeleteCsvExportDialog';
import RenameCsvExportDialog from './RenameCsvExportDialog';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import TechnologyTags from 'components/TechnologyTags';
import CountryTags from 'components/CountryTags';
import DefintionsList, { Term, Definiton } from 'components/DefintionsList';
import { ATTRIBUTE_DESCRIPTORS } from '@wooindex/common/attributes';

const TAGS_LIMIT = 3;

function getChipIcon (status: CsvExportRequestStatus) {
  switch (status) {
    case 'QUEUED': return <QueuedIcon />;
    case 'STARTED': return <CircularProgress color='inherit' size={16} />;
    case 'FINISHED': return <FinishedIcon />;
  }
}

function downloadable (csvExportRequest: CsvExportRequest) {
  return csvExportRequest.status === 'FINISHED';
}

function removable (csvExportRequest: CsvExportRequest) {
  return csvExportRequest.status === 'QUEUED' || csvExportRequest.status === 'FINISHED';
}

const useStyles = makeStyles(theme => ({
  summaryContent: {
    alignItems: 'center'
  },
  statusColumn: {
    flexBasis: 115
  },
  summaryDate: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary
  },
  hidden: {
    visibility: 'hidden'
  },
  filterContainer: {
    margin: theme.spacing(2, 0, 1, 0)
  },
  columnsDisplay: {
    marginTop: theme.spacing(2)
  }
}));

interface CsvExportRequestItemProps {
  expanded?: boolean
  onExpand?: (isExpanded: boolean) => void
  onDelete?: (csvExportRequest: CsvExportRequest) => void
  onRename?: (csvExportRequest: CsvExportRequest) => void
  csvExportRequest?: CsvExportRequest
}

function downloadLink (csvExportRequest: CsvExportRequest, format: 'csv' | 'xlsx') {
  return `/api/csv-exports/${encodeURIComponent(csvExportRequest.id)}/data?format=${encodeURIComponent(format)}`;
}

function getDownloadButtonProps (csvExportRequest?: CsvExportRequest, format: 'csv' | 'xlsx' = 'csv') {
  if (csvExportRequest) {
    return {
      component: 'a',
      href: downloadLink(csvExportRequest, format),
      disabled: !downloadable(csvExportRequest),
      download: csvExportRequest.name
    };
  } else {
    return { disabled: true };
  }
}

interface DownloadMenuProps {
  csvExportRequest?: CsvExportRequest
  onClose?: (event: React.SyntheticEvent) => void
  anchorElm?: null | HTMLElement
}

function DownloadMenu ({ csvExportRequest, anchorElm, onClose }: DownloadMenuProps) {
  return (
    <Menu open={!!anchorElm} anchorEl={anchorElm} onClose={onClose} onClick={onClose}>
      <MenuItem {...getDownloadButtonProps(csvExportRequest, 'csv')}>
        <ListItemIcon>
          <DownloadIcon fontSize='small' />
        </ListItemIcon>
        <ListItemText primary='.CSV' />
      </MenuItem>
      <MenuItem {...getDownloadButtonProps(csvExportRequest, 'xlsx')}>
        <ListItemIcon>
          <DownloadIcon fontSize='small' />
        </ListItemIcon>
        <ListItemText primary='.XLSX' />
      </MenuItem>
    </Menu>
  );
}

function reuseFilterLink (csvExportRequest: CsvExportRequest): string {
  const searchParams = serializeFilter(csvExportRequest.filter);
  searchParams.set('basedOn', String(csvExportRequest.id));
  return `/dashboard?${searchParams}`;
}

function CsvExportRequestItem ({
  csvExportRequest,
  expanded = false,
  onExpand = () => {},
  onDelete = () => {},
  onRename = () => {}
}: CsvExportRequestItemProps) {
  const classes = useStyles();
  const { dateTimeFormat, numberFormat } = useI18n();
  const canSaveTodrive = useHasFeature('saveTodrive');
  const handleExpand = React.useCallback((event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    if (onExpand) {
      onExpand(isExpanded);
    }
  }, [onExpand]);

  const [downloadBtnAnchorElm, setDownloadBtnAnchorElm] = React.useState<HTMLButtonElement | null>(null);

  const handleDownloadMenuClose = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    setDownloadBtnAnchorElm(null);
  };

  const downloadButtonProps = {
    disabled: !csvExportRequest || !downloadable(csvExportRequest),
    onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();
      setDownloadBtnAnchorElm(event.currentTarget);
    }
  };

  return (
    <Accordion expanded={expanded} onChange={handleExpand}>
      <AccordionSummary
        classes={{ content: classes.summaryContent }}
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel1bh-content'
        id={csvExportRequest && `export-${csvExportRequest.id}-header`}
      >
        <div className={classes.statusColumn}>
          <Chip
            size='small'
            variant='outlined'
            icon={getChipIcon(csvExportRequest ? csvExportRequest.status : 'STARTED')}
            label={csvExportRequest ? csvExportRequest.status : 'LOADING'}
          />
        </div>
        <Typography>
          {csvExportRequest ? csvExportRequest.name : <Skeleton width={300} />}
        </Typography>
        <FlexFill />
        <Typography
          className={clsx(classes.summaryDate, { [classes.hidden]: expanded })}
        >
          {csvExportRequest
            ? dateTimeFormat.medium.format(csvExportRequest.queuedAt)
            : <Skeleton width={150} />}
        </Typography>
        <IconButton
          className={expanded ? classes.hidden : undefined}
          onFocus={(event: React.FocusEvent) => event.stopPropagation()}
          {...downloadButtonProps}
        >
          <DownloadIcon fontSize='small' />
        </IconButton>
      </AccordionSummary>
      {csvExportRequest
        ? (
          <>
            <AccordionDetails style={{ flexDirection: 'column' }}>
              <Divider />
              <DefintionsList className={classes.filterContainer}>
                <Term>countries:</Term>
                <Definiton>
                  {csvExportRequest.filter.countries && csvExportRequest.filter.countries.length > 0
                    ? <CountryTags limit={TAGS_LIMIT} value={csvExportRequest.filter.countries} />
                    : null}
                </Definiton>

                <Term>technologies:</Term>
                <Definiton>
                  {csvExportRequest.filter.technologies && csvExportRequest.filter.technologies.length > 0
                    ? <TechnologyTags limit={TAGS_LIMIT} value={csvExportRequest.filter.technologies} />
                    : null}
                </Definiton>

                <Term>search:</Term>
                <Definiton>
                  {csvExportRequest.filter.search || null}
                </Definiton>

                <Term>url:</Term>
                <Definiton>
                  {csvExportRequest.filter.url && csvExportRequest.filter.url.length > 0 ? csvExportRequest.filter.url.join(' ') : null}
                </Definiton>

                <Divider />

                <Term>Columns:</Term>
                <Definiton>
                  {csvExportRequest.columns.map(column => ATTRIBUTE_DESCRIPTORS[column]?.name ?? column).join(', ')}
                </Definiton>

                <Term>File size:</Term>
                <Definiton>
                  {typeof csvExportRequest.byteSize === 'number' ? numberFormat.digital.format(csvExportRequest.byteSize) : '-'}
                </Definiton>

                <Term>Rows count:</Term>
                <Definiton>
                  {typeof csvExportRequest.rowsCount === 'number' ? numberFormat.basic.format(csvExportRequest.rowsCount) : '-'}
                </Definiton>
              </DefintionsList>
            </AccordionDetails>
            <Divider />
            <AccordionActions>
              {removable(csvExportRequest)
                ? (
                  <Button
                    size='small'
                    startIcon={<DeleteIcon />}
                    onClick={() => onDelete(csvExportRequest)}
                  >
                    {csvExportRequest.status === 'QUEUED' ? 'Cancel' : 'Remove'}
                  </Button>
                  )
                : null}
              <Button
                size='small'
                startIcon={<EditIcon />}
                onClick={onRename && (() => onRename(csvExportRequest))}
              >
                Rename
              </Button>
              <Button
                size='small'
                startIcon={<FilterIcon />}
                component={Link}
                href={reuseFilterLink(csvExportRequest)}
              >
                Use Filter
              </Button>
              <Button
                color='primary'
                variant='contained'
                {...downloadButtonProps}
                endIcon={<ArrowDropDownIcon />}
              >
                Download
              </Button>
              {canSaveTodrive && downloadable(csvExportRequest)
                ? <SaveToDriveButton src={downloadLink(csvExportRequest, 'csv')} filename='data-export.csv' />
                : null}

            </AccordionActions>
          </>
          )
        : null}
      <DownloadMenu
        csvExportRequest={csvExportRequest}
        anchorElm={downloadBtnAnchorElm}
        onClose={handleDownloadMenuClose}
      />
    </Accordion>
  );
}

interface CsvExportRequestsTableProps {
  csvExportRequests?: CsvExportRequestList
  className?: string
  loading?: boolean
  refresh?: () => void
  page: number
  onPageChange: (page: number) => void
  rowsPerPage: number
  onRowsPerPageChange: (rowsPerPAge: number) => void
}

export default function CsvExportRequestsTable ({
  className,
  csvExportRequests,
  loading,
  refresh = () => {},
  page = 0,
  rowsPerPage = 25,
  onPageChange = () => {},
  onRowsPerPageChange = () => {}
}: CsvExportRequestsTableProps) {
  const [expanded, setExpanded] = React.useState<number | null>(null);
  const [toDeleteItem, setToDeleteItem] = React.useState<CsvExportRequest>();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [toRenameItem, setToRenameItem] = React.useState<CsvExportRequest>();
  const [renameDialogOpen, setRenameDialogOpen] = React.useState(false);

  const handleChange = (panel: number) => (isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : null);
  };

  const handleDeleteDialogClose = (deleted: boolean) => {
    setDeleteDialogOpen(false);
    if (deleted) {
      refresh();
    }
  };

  const handleDeleteClick = (csvExportRequest: CsvExportRequest) => {
    setToDeleteItem(csvExportRequest);
    setDeleteDialogOpen(true);
  };

  const handleRenameDialogClose = (renamed: boolean) => {
    setRenameDialogOpen(false);
    if (renamed) {
      refresh();
    }
  };

  const handleRenameClick = (csvExportRequest: CsvExportRequest) => {
    setToRenameItem(csvExportRequest);
    setRenameDialogOpen(true);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onRowsPerPageChange(parseInt(event.target.value, 10));
    onPageChange(0);
  };

  const items = csvExportRequests?.items || [];

  return (
    <div className={className}>
      {toDeleteItem && <DeleteCsvExportDialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} item={toDeleteItem} />}
      {toRenameItem && <RenameCsvExportDialog open={renameDialogOpen} onClose={handleRenameDialogClose} item={toRenameItem} />}
      {loading
        ? new Array(3).fill(null).map((x, i) => <CsvExportRequestItem key={i} />)
        : items.length <= 0
          ? (
            <>
              Head over to <Link href='/dashboard'>the dashboard</Link> to create your first export.
            </>
            )
          : (
            <>
              <div>
                {items.map((csvExportRequest) => {
                  const isExpanded = expanded === csvExportRequest.id;
                  return (
                    <CsvExportRequestItem
                      key={csvExportRequest.id}
                      csvExportRequest={csvExportRequest}
                      expanded={isExpanded}
                      onExpand={handleChange(csvExportRequest.id)}
                      onDelete={handleDeleteClick}
                      onRename={handleRenameClick}
                    />
                  );
                })}
              </div>
              <TablePagination
                component='div'
                count={csvExportRequests?.total || -1}
                page={page}
                onPageChange={(event, page) => onPageChange(page)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
              />
            </>
            )}
    </div>
  );
}
